import { generateUuid } from '@lib/uuid';
import {
  AllowAllPermissions,
  ChatsDashboardMenuItem,
  FilesMenuItem,
  LinkMenuItem,
  MenuItem,
  MenuItemType,
  NoteMenuItem,
  NotesFolderMenuItem,
  PortalDashboardMenuItemType,
  PortalPageMenuItem,
  PortalProcessMenuItem,
  PortalSectionMenuItem,
  TasksDashboardMenuItem,
} from '@nx-frontend/shared/types';
import { calcProcessData } from '@nx-frontend/shared/utils';

export const PortalFilesDashboardSlug = 'files-dashboard';
export const PortalTasksDashboardSlug = 'tasks-dashboard';
export const PortalChatsDashboardSlug = 'chats-dashboard';

export function generateNotesFolderMenuItem(params: {
  name: string;
  targetId: string;
  url: string;
  openInNewTab: boolean;
  index: number;
  parentId?: string;
  pageId?: string;
}): NotesFolderMenuItem {
  return {
    type: MenuItemType.NOTES_FOLDER,
    id: generateUuid(),
    parentId: params.parentId ?? '',
    hasChildren: false,
    targetId: params.targetId,
    icon: 'folder',
    name: params.name,
    url: params.url,
    slug: getSlugFromUrl(params.url),
    index: params.index,
    order: [],
    visible: true,
    openInNewTab: params.openInNewTab,
    pageId: params.pageId,
    isDraft: true,
    permissions: AllowAllPermissions,
  };
}

export function generateSectionMenuItem(params: {
  name: string;
  url: string;
  openInNewTab: boolean;
  index: number;
  parentId?: string;
  pageId?: string;
  visible?: boolean;
}): PortalSectionMenuItem {
  return {
    type: MenuItemType.PORTAL_SECTION,
    id: generateUuid(),
    parentId: params.parentId ?? '',
    hasChildren: false,
    icon: 'folder',
    name: params.name,
    url: params.url,
    slug: getSlugFromUrl(params.url),
    index: params.index,
    order: [],
    visible: params.visible === undefined ? true : params.visible,
    openInNewTab: params.openInNewTab,
    pageId: params.pageId,
    isDraft: true,
    permissions: AllowAllPermissions,
  };
}

export function generateProcessMenuItem(params: {
  name: string;
  url: string;
  openInNewTab: boolean;
  index: number;
  parentId?: string;
  pageId?: string;
  sequential?: boolean;
  visible?: boolean;
}): PortalProcessMenuItem {
  return {
    type: MenuItemType.PORTAL_PROCESS,
    id: generateUuid(),
    parentId: params.parentId ?? '',
    hasChildren: false,
    icon: 'process',
    name: params.name,
    url: params.url,
    slug: getSlugFromUrl(params.url),
    index: params.index,
    order: [],
    visible: params.visible === undefined ? true : params.visible,
    openInNewTab: params.openInNewTab,
    pageId: params.pageId,
    isDraft: true,
    permissions: AllowAllPermissions,
    process: calcProcessData({ status: 'todo', items: [], sequential: params.sequential || false }),
  };
}

export function generatePageMenuItem(params: {
  name: string;
  url: string;
  openInNewTab: boolean;
  index: number;
  parentId?: string;
  pageId?: string;
  visible?: boolean;
}): PortalPageMenuItem {
  return {
    type: MenuItemType.PORTAL_PAGE,
    id: generateUuid(),
    parentId: params.parentId ?? '',
    hasChildren: false,
    icon: 'note',
    name: params.name,
    url: params.url,
    slug: getSlugFromUrl(params.url),
    index: params.index,
    visible: params.visible === undefined ? true : params.visible,
    openInNewTab: params.openInNewTab,
    pageId: params.pageId,
    isDraft: true,
    permissions: AllowAllPermissions,
  };
}

export function generateNoteMenuItem(params: {
  name: string;
  url: string;
  openInNewTab: boolean;
  index: number;
  targetId: string;
  parentId?: string;
  pageId?: string;
}): NoteMenuItem {
  return {
    type: MenuItemType.NOTE,
    id: generateUuid(),
    parentId: params.parentId ?? '',
    hasChildren: false,
    targetId: params.targetId,
    icon: 'note',
    name: params.name,
    url: params.url,
    slug: getSlugFromUrl(params.url),
    index: params.index,
    visible: true,
    openInNewTab: params.openInNewTab,
    pageId: params.pageId,
    isDraft: true,
    permissions: AllowAllPermissions,
  };
}

export function generateLinkMenuItem(params: {
  name: string;
  url: string;
  index: number;
  openInNewTab?: boolean;
  parentId?: string;
  visible?: boolean;
}): LinkMenuItem {
  return {
    type: MenuItemType.LINK,
    id: generateUuid(),
    parentId: params.parentId ?? '',
    hasChildren: false,
    icon: 'custom_link',
    name: params.name,
    url: params.url,
    slug: '',
    index: params.index,
    visible: params.visible === undefined ? true : params.visible,
    openInNewTab: params.openInNewTab ?? true,
    isDraft: true,
    permissions: AllowAllPermissions,
  };
}

export function generateFileMenuItem(params: { index: number; parentId?: string; pageId?: string }): FilesMenuItem {
  return {
    type: MenuItemType.FILES,
    id: generateUuid(),
    parentId: params.parentId ?? '',
    hasChildren: false,
    icon: 'file',
    name: 'Files',
    url: `/${PortalFilesDashboardSlug}`,
    slug: PortalFilesDashboardSlug,
    index: params.index,
    visible: true,
    openInNewTab: false,
    pageId: params.pageId,
    isDraft: true,
    permissions: AllowAllPermissions,
  };
}

export function generateTasksMenuItem(params: {
  index: number;
  parentId?: string;
  pageId?: string;
}): TasksDashboardMenuItem {
  return {
    type: MenuItemType.TASKS_DASHBOARD,
    id: generateUuid(),
    parentId: params.parentId ?? '',
    hasChildren: false,
    icon: 'tasks',
    name: 'Task Dashboard',
    url: `/${PortalTasksDashboardSlug}`,
    slug: PortalTasksDashboardSlug,
    index: params.index,
    visible: true,
    openInNewTab: false,
    pageId: params.pageId,
    isDraft: true,
    permissions: AllowAllPermissions,
  };
}

export function generateChatsDashboardMenuItem(params: {
  index: number;
  parentId?: string;
  pageId?: string;
}): ChatsDashboardMenuItem {
  return {
    type: MenuItemType.CHATS_DASHBOARD,
    id: generateUuid(),
    parentId: params.parentId ?? '',
    hasChildren: false,
    icon: 'chat',
    name: 'Chat Dashboard',
    url: `/${PortalChatsDashboardSlug}`,
    slug: PortalChatsDashboardSlug,
    index: params.index,
    visible: true,
    openInNewTab: false,
    pageId: params.pageId,
    isDraft: true,
    permissions: AllowAllPermissions,
  };
}

export function generatePortalDashboardMenuItem(
  menuItemType: PortalDashboardMenuItemType,
  params: {
    index: number;
    parentId?: string;
    pageId?: string;
  }
): MenuItem {
  switch (menuItemType) {
    case MenuItemType.CHATS_DASHBOARD:
      return generateChatsDashboardMenuItem({ parentId: params.parentId, index: params.index, pageId: params.pageId });
    case MenuItemType.TASKS_DASHBOARD:
      return generateTasksMenuItem({ parentId: params.parentId, index: params.index, pageId: params.pageId });
    case MenuItemType.FILES:
      return generateFileMenuItem({ parentId: params.parentId, index: params.index, pageId: params.pageId });
  }
}

export function getSlugFromUrl(url: string): string {
  const pathComponents = url.split('/');
  return pathComponents[pathComponents.length - 1];
}
